import React, { useState, useRef, useEffect } from 'react';
import './App.css';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  type?: 'text' | 'image' | 'video' | 'code' | 'graph' | 'browser' | 'file';
  data?: any;
  timestamp: Date;
}

interface Attachment {
  id: string;
  name: string;
  type: string;
  size: number;
  data?: string;
}

const App: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState('');
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTTSEnabled, setIsTTSEnabled] = useState(false);
  const [showTools, setShowTools] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!inputText.trim() && attachments.length === 0) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: inputText,
      type: 'text',
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsLoading(true);

    try {
      // Determine the type of request based on content
      const requestType = analyzeRequest(inputText);
      
      const payload = {
        message: inputText,
        attachments: attachments.map(a => ({
          name: a.name,
          type: a.type,
          data: a.data
        })),
        context: {
          conversation_history: messages.slice(-10),
          request_type: requestType
        }
      };

      // Route to appropriate endpoint based on request type
      let endpoint = '/chat';
      let requestBody: any = { message: inputText };
      
      if (requestType === 'image') {
        endpoint = '/image/generate';
        requestBody = { prompt: inputText };
      } else if (requestType === 'video') {
        endpoint = '/video/generate';
        requestBody = { prompt: inputText };
      } else if (requestType === 'search') {
        endpoint = '/search';
        requestBody = { query: inputText };
      } else if (requestType === 'browser') {
        endpoint = '/browser';
        requestBody = { url: inputText };
      }
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) throw new Error('Request failed');

      const data = await response.json();
      
      // Determine response type and format accordingly
      let messageType: Message['type'] = 'text';
      let messageContent = data.response || data.text || 'Processing complete';
      let messageData: any = {};
      
      if (requestType === 'image' && data.image_url) {
        messageType = 'image';
        messageContent = 'Here\'s your generated image:';
        messageData = { url: data.image_url };
      } else if (requestType === 'video' && data.video_url) {
        messageType = 'video';
        messageContent = 'Here\'s your generated video:';
        messageData = { url: data.video_url };
      } else if (requestType === 'browser' && data.screenshot) {
        messageType = 'browser';
        messageContent = data.content || 'Browser automation complete';
        messageData = { screenshot: data.screenshot, html: data.html };
      } else if (requestType === 'search' && data.results) {
        messageContent = `Found ${data.results.length} results:\n${data.results.map((r: any) => `• ${r.title}`).join('\n')}`;
      }
      
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: messageContent,
        type: messageType,
        data: messageData,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMessage]);
      
      // Handle TTS if enabled
      if (isTTSEnabled && assistantMessage.type === 'text') {
        speakText(assistantMessage.content);
      }

    } catch (error) {
      console.error('Error:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'I encountered an error processing your request. Please try again.',
        type: 'text',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      setAttachments([]);
    }
  };

  const analyzeRequest = (text: string): string => {
    const lowerText = text.toLowerCase();
    
    if (lowerText.includes('image') || lowerText.includes('picture') || lowerText.includes('draw') || lowerText.includes('generate') || lowerText.includes('create')) {
      return 'image';
    }
    if (lowerText.includes('video') || lowerText.includes('animate') || lowerText.includes('motion')) {
      return 'video';
    }
    if (lowerText.includes('browse') || lowerText.includes('website') || lowerText.includes('scrape') || lowerText.includes('navigate')) {
      return 'browser';
    }
    if (lowerText.includes('code') || lowerText.includes('program') || lowerText.includes('function') || lowerText.includes('debug')) {
      return 'code';
    }
    if (lowerText.includes('graph') || lowerText.includes('chart') || lowerText.includes('plot') || lowerText.includes('visualize')) {
      return 'graph';
    }
    if (lowerText.includes('search') || lowerText.includes('find') || lowerText.includes('look up')) {
      return 'search';
    }
    if (lowerText.includes('remember') || lowerText.includes('recall') || lowerText.includes('memory')) {
      return 'memory';
    }
    
    return 'chat';
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    Array.from(files).forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const attachment: Attachment = {
          id: Date.now().toString() + Math.random(),
          name: file.name,
          type: file.type,
          size: file.size,
          data: e.target?.result as string
        };
        setAttachments(prev => [...prev, attachment]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeAttachment = (id: string) => {
    setAttachments(prev => prev.filter(a => a.id !== id));
  };

  const toggleRecording = async () => {
    if (!isRecording) {
      // Start recording
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // Implement recording logic here
        setIsRecording(true);
      } catch (error) {
        console.error('Error accessing microphone:', error);
        alert('Could not access microphone. Please check permissions.');
      }
    } else {
      // Stop recording
      setIsRecording(false);
      // Process and send audio
    }
  };

  const speakText = (text: string) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      window.speechSynthesis.speak(utterance);
    }
  };

  const renderMessage = (message: Message) => {
    switch (message.type) {
      case 'image':
        return (
          <div className="message-content">
            <p>{message.content}</p>
            {message.data?.url && (
              <img src={message.data.url} alt="Generated" className="message-image" />
            )}
          </div>
        );
      
      case 'video':
        return (
          <div className="message-content">
            <p>{message.content}</p>
            {message.data?.url && (
              <video src={message.data.url} controls className="message-video" />
            )}
          </div>
        );
      
      case 'code':
        return (
          <div className="message-content">
            <p>{message.content}</p>
            {message.data?.code && (
              <pre className="message-code">
                <code>{message.data.code}</code>
              </pre>
            )}
          </div>
        );
      
      case 'graph':
        return (
          <div className="message-content">
            <p>{message.content}</p>
            {message.data?.chart && (
              <div className="message-graph" dangerouslySetInnerHTML={{ __html: message.data.chart }} />
            )}
          </div>
        );
      
      case 'browser':
        return (
          <div className="message-content">
            <p>{message.content}</p>
            {message.data?.screenshot && (
              <img src={message.data.screenshot} alt="Browser screenshot" className="message-screenshot" />
            )}
            {message.data?.html && (
              <div className="message-browser-content">
                <pre>{message.data.html.substring(0, 500)}...</pre>
              </div>
            )}
          </div>
        );
      
      default:
        return <p className="message-content">{message.content}</p>;
    }
  };

  return (
    <div className="app">
      <div className="header">
        <div className="logo">
          <span className="logo-icon">🧠</span>
          <span className="logo-text">LexOS Multimodal</span>
        </div>
        <div className="header-controls">
          <button 
            className={`tool-button ${showTools ? 'active' : ''}`}
            onClick={() => setShowTools(!showTools)}
          >
            <span className="tool-icon">🛠️</span>
            Tools
          </button>
          <div className="status-indicator">
            <span className="status-dot"></span>
            <span>Connected</span>
          </div>
        </div>
      </div>

      {showTools && (
        <div className="tools-panel">
          <div className="tool-card">
            <span className="tool-emoji">💬</span>
            <span>Chat</span>
          </div>
          <div className="tool-card">
            <span className="tool-emoji">🎨</span>
            <span>Image Gen</span>
          </div>
          <div className="tool-card">
            <span className="tool-emoji">🎬</span>
            <span>Video</span>
          </div>
          <div className="tool-card">
            <span className="tool-emoji">🌐</span>
            <span>Browser</span>
          </div>
          <div className="tool-card">
            <span className="tool-emoji">📊</span>
            <span>Graphs</span>
          </div>
          <div className="tool-card">
            <span className="tool-emoji">💻</span>
            <span>Code</span>
          </div>
          <div className="tool-card">
            <span className="tool-emoji">🔍</span>
            <span>Search</span>
          </div>
          <div className="tool-card">
            <span className="tool-emoji">🧠</span>
            <span>Memory</span>
          </div>
        </div>
      )}

      <div className="chat-container" ref={chatContainerRef}>
        <div className="messages-area">
          {messages.length === 0 ? (
            <div className="welcome-message">
              <h2>Welcome to LexOS Multimodal</h2>
              <p>I can help you with:</p>
              <div className="capabilities">
                <div className="capability">💬 Natural conversations</div>
                <div className="capability">🎨 Image generation</div>
                <div className="capability">🎬 Video creation</div>
                <div className="capability">🌐 Web browsing & automation</div>
                <div className="capability">📊 Data visualization</div>
                <div className="capability">💻 Code generation</div>
                <div className="capability">🔍 Web search</div>
                <div className="capability">🎙️ Voice interaction</div>
              </div>
              <p className="hint">Try asking me anything or upload a file to get started!</p>
            </div>
          ) : (
            messages.map((message) => (
              <div key={message.id} className={`message ${message.role}`}>
                <div className="message-header">
                  <span className="message-role">
                    {message.role === 'user' ? '👤' : '🤖'}
                  </span>
                  <span className="message-time">
                    {message.timestamp.toLocaleTimeString()}
                  </span>
                </div>
                {renderMessage(message)}
              </div>
            ))
          )}
          {isLoading && (
            <div className="message assistant loading">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      <div className="input-container">
        {attachments.length > 0 && (
          <div className="attachments-preview">
            {attachments.map(attachment => (
              <div key={attachment.id} className="attachment-item">
                <span className="attachment-name">{attachment.name}</span>
                <button 
                  className="remove-attachment"
                  onClick={() => removeAttachment(attachment.id)}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
        
        <div className="input-controls">
          <button 
            className="control-button"
            onClick={() => fileInputRef.current?.click()}
            title="Attach files"
          >
            📎
          </button>
          
          <button 
            className={`control-button ${isRecording ? 'recording' : ''}`}
            onClick={toggleRecording}
            title="Voice input"
          >
            {isRecording ? '🔴' : '🎙️'}
          </button>
          
          <button 
            className={`control-button ${isTTSEnabled ? 'active' : ''}`}
            onClick={() => setIsTTSEnabled(!isTTSEnabled)}
            title="Text to speech"
          >
            {isTTSEnabled ? '🔊' : '🔇'}
          </button>
          
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileUpload}
            style={{ display: 'none' }}
            accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt,.csv,.json"
          />
          
          <textarea
            className="message-input"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder="Ask me anything... I can generate images, browse the web, write code, and much more!"
            rows={1}
          />
          
          <button 
            className="send-button"
            onClick={handleSend}
            disabled={isLoading || (!inputText.trim() && attachments.length === 0)}
          >
            {isLoading ? '⏳' : '➤'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default App;